/** A point as fractions of the image's width and height (0–1). */
export interface Pt {
  x: number;
  y: number;
}

/** Corners of a print, clockwise from top-left. */
export interface Quad {
  tl: Pt;
  tr: Pt;
  br: Pt;
  bl: Pt;
}

/** What the corner handles start at when nothing was detected. */
export const DEFAULT_QUAD: Quad = {
  tl: { x: 0.08, y: 0.08 },
  tr: { x: 0.92, y: 0.08 },
  br: { x: 0.92, y: 0.92 },
  bl: { x: 0.08, y: 0.92 },
};

export function quadPoints(q: Quad): Pt[] {
  return [q.tl, q.tr, q.br, q.bl];
}

/** Shoelace area in normalised units (1 = the whole frame). */
export function quadArea(q: Quad): number {
  const p = quadPoints(q);
  let a = 0;
  for (let i = 0; i < 4; i++) {
    const j = (i + 1) % 4;
    a += p[i]!.x * p[j]!.y - p[j]!.x * p[i]!.y;
  }
  return Math.abs(a) / 2;
}

/** True when the four corners turn the same way, i.e. no corner is dragged
 *  across another into a bow-tie the warp can't make sense of. */
export function isConvex(q: Quad): boolean {
  const p = quadPoints(q);
  let sign = 0;
  for (let i = 0; i < 4; i++) {
    const a = p[i]!;
    const b = p[(i + 1) % 4]!;
    const c = p[(i + 2) % 4]!;
    const cross = (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
    if (cross === 0) continue;
    const s = Math.sign(cross);
    if (sign === 0) sign = s;
    else if (s !== sign) return false;
  }
  return true;
}

/** Blend towards `next`, so the live outline doesn't jitter frame to frame. */
export function smoothQuad(prev: Quad | null, next: Quad, keep = 0.6): Quad {
  if (!prev) return next;
  const mix = (a: Pt, b: Pt): Pt => ({
    x: a.x * keep + b.x * (1 - keep),
    y: a.y * keep + b.y * (1 - keep),
  });
  return {
    tl: mix(prev.tl, next.tl),
    tr: mix(prev.tr, next.tr),
    br: mix(prev.br, next.br),
    bl: mix(prev.bl, next.bl),
  };
}
